import React, { useEffect, useState } from 'react';
import { gradeApi } from '../../../../api.js';
import { useAuth } from '../../../../auth/AuthContext';
import { toast } from 'react-hot-toast';
import {
  CheckCircle,
  Code,
  Cpu,
  FileText,
  ArrowLeft,
  Save,
  HelpCircle,
  X,
  ExternalLink
} from 'lucide-react';
import PDFProcessor from '../components/PDFProcessor';
import '../styles/globals.scss';
import './style.scss';
import './page.scss';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SolutionUploadComponent from '../components/SolutionUploadComponent';
import RubricViewer from '../components/RubricViewer';
import { useParams } from 'react-router-dom';
import {
  clearAssignment,
  clearSubmissions,
  getAssignmentDetails
} from '../../../../store/teacherSlice';
import ConfirmationModal from '../../../ConfirmationModal';

// Essay Guidance Modal Component
const EssayGuidanceModal = ({ isOpen, onClose }) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        width: '100vw',
        height: '100vh'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          position: 'relative',
          textAlign: 'left'
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#888',
            padding: '8px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
          onClick={onClose}
          onMouseOver={e => {
            e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            e.target.style.color = '#555';
          }}
          onMouseOut={e => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = '#888';
          }}
        >
          <X size={20} />
        </button>

        <h3
          style={{
            color: '#6b4a2a',
            marginTop: 0,
            marginBottom: '16px',
            fontSize: '1.3rem',
            fontWeight: '600',
            borderBottom: '1px solid rgba(228, 177, 122, 0.3)',
            paddingBottom: '12px',
            textAlign: 'left'
          }}
        >
          Grading Essays with GradeWiz
        </h3>

        <div style={{ marginBottom: '20px' }}>
          <h4
            style={{
              color: '#7a5d42',
              marginBottom: '8px',
              fontSize: '1.1rem',
              fontWeight: '600',
              textAlign: 'left'
            }}
          >
            How to Get Started:
          </h4>
          <p
            style={{
              color: '#4b5563',
              lineHeight: '1.6',
              margin: '0 0 12px 0',
              textAlign: 'left'
            }}
          >
            To grade essays, simply upload your grading rubric or criteria document in the upload
            area. The system will use your rubric to evaluate student essays.
          </p>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4
            style={{
              color: '#7a5d42',
              marginBottom: '8px',
              fontSize: '1.1rem',
              fontWeight: '600',
              textAlign: 'left'
            }}
          >
            Tips for Effective Essay Grading:
          </h4>
          <ol
            style={{
              color: '#4b5563',
              lineHeight: '1.6',
              paddingLeft: '25px',
              margin: '1',
              textAlign: 'left'
            }}
          >
            <li
              style={{
                marginBottom: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(228, 177, 122, 0.15)'
              }}
            >
              Include clear point values for each section of your rubric
            </li>
            <li
              style={{
                marginBottom: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(228, 177, 122, 0.15)'
              }}
            >
              Specify key elements you're looking for in student responses
            </li>
            <li
              style={{
                marginBottom: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(228, 177, 122, 0.15)'
              }}
            >
              Include any specific formatting or citation requirements
            </li>

            <li>Disable "Advanced Vision" feature at the next page</li>
          </ol>
        </div>

        <div style={{ textAlign: 'center' }}>
          <button
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'inline-block',
              boxShadow: '0 2px 8px rgba(228, 177, 122, 0.3)'
            }}
            onClick={onClose}
            onMouseOver={e => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(228, 177, 122, 0.4)';
            }}
            onMouseOut={e => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(228, 177, 122, 0.3)';
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

// Examples Modal Component
const ExamplesModal = ({ isOpen, onClose }) => {
  const [enlargedImage, setEnlargedImage] = useState(null);

  if (!isOpen) {
    return null;
  }

  const examples = [
    {
      level: 'Great',
      color: '#4CAF50',
      image: 'https://cdn.jsdelivr.net/gh/maxbohun/gradewiz-images@main/tutorial/great.png',
      description:
        "Perfect fit: has some calculations and explanations but doesn't rely on complicated visual drawings. GradeWiz will be able to grade this perfectly."
    },
    {
      level: 'Good',
      color: '#FF9800',
      image: 'https://cdn.jsdelivr.net/gh/maxbohun/gradewiz-images/tutorial/medium.png',
      description:
        'Mostly a good fit, but fairly cramped with little space allotted for students making them write tiny characters that might be very hard to read. Overall GradeWiz will do a really good job but might require some manual corrections.'
    },
    {
      level: 'Bad',
      color: '#F44336',
      image: 'https://cdn.jsdelivr.net/gh/maxbohun/gradewiz-images@main/tutorial/bad.png',
      description:
        "Very heavily visual, relies on colors/hard to see markings/drawing complicated shapes. This question will have to be graded manually by the teacher if it's part of a larger assignment (but GradeWiz might handle the other questions really well!)."
    }
  ];

  const handleImageClick = image => {
    setEnlargedImage(image);
  };

  const closeEnlargedImage = e => {
    e.stopPropagation();
    setEnlargedImage(null);
  };

  return (
    <div className="examples-modal-overlay" onClick={onClose}>
      <div className="examples-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Assignment Compatibility Examples</h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="examples-grid">
          {examples.map((example, index) => (
            <div key={index} className="example-card">
              <div className="example-header">
                <div className="level-badge" style={{ backgroundColor: example.color }}>
                  {example.level}
                </div>
              </div>

              <div className="example-image">
                <img
                  src={example.image}
                  alt={`${example.level} example`}
                  loading="lazy"
                  style={{ cursor: 'pointer' }}
                  title="Click to enlarge"
                  onClick={() => handleImageClick(example.image)}
                />
              </div>

              <div className="example-description">
                <p>{example.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="modal-footer">
          <p>
            <strong>Tip:</strong> Even if your assignment has some "Bad" questions, GradeWiz can
            still help grade the compatible parts automatically!
          </p>
        </div>
      </div>

      {/* Enlarged image viewer */}
      {enlargedImage && (
        <div
          className="enlarged-image-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            cursor: 'zoom-out'
          }}
          onClick={closeEnlargedImage}
        >
          <img
            src={enlargedImage}
            alt="Enlarged example"
            style={{
              maxWidth: '90%',
              maxHeight: '90vh',
              objectFit: 'contain',
              border: '2px solid white',
              borderRadius: '4px',
              boxShadow: '0 5px 20px rgba(0, 0, 0, 0.5)'
            }}
          />
          <button
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onClick={closeEnlargedImage}
          >
            <X size={24} />
          </button>
        </div>
      )}
    </div>
  );
};

export default function PDFAssignmentHome() {
  const [pdfFiles, setPdfFiles] = useState({
    withSolutions: null
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [proceed, setProceed] = useState(false);
  const { assignment_id, class_id } = useParams();
  const [uploadHover, setUploadHover] = useState(false);
  const dispatch = useDispatch();
  const [uploadStatus, setUploadStatus] = useState({
    withSolutions: false
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showExamplesModal, setShowExamplesModal] = useState(false);
  const [showEssayGuidanceModal, setShowEssayGuidanceModal] = useState(false);

  const [isUploading, setIsUploading] = useState({
    withSolutions: false
  });

  const {
    data: assignment,
    isLoading: isAssignmentLoading,
    errorMessage: assignmentError
  } = useSelector(state => state?.teacherReducer?.assignment);

  // ... (keep all the existing useEffect hooks and functions exactly as they are)
  const getAssignment = () =>
    dispatch(
      getAssignmentDetails({
        assignmentId: assignment_id,
        token: user.session_token,
        classId: class_id
      })
    );

  // Current progress data for saving
  const progressData = {
    solution_pdf_url: pdfFiles?.withSolutions
  };

  useEffect(() => {
    // This effect runs once on component mount and handles initial redirect logic
    const redirectBasedOnProgress = async () => {
      try {
        // Fetch the latest progress data
        const response = await gradeApi.get({
          path: `/api/assignment/${assignment_id}/load-progress/`,
          config: {
            headers: {
              Authorization: `Bearer ${user.session_token}`
            }
          }
        });

        if (!response.data) {
          console.log('No saved progress found');
          return;
        }

        const { creation_step } = response.data;
        console.log(`Loaded progress with creation_step: ${creation_step}`);

        // Handle redirects based on the creation_step
        if (creation_step >= 4 && response.data.question_boxes) {
          try {
            // Check if question_boxes contains extracted questions
            const questionData =
              typeof response.data.question_boxes === 'string'
                ? JSON.parse(response.data.question_boxes)
                : response.data.question_boxes;

            // If there's question data that looks like rubric questions, set proceed
            if (questionData && Object.keys(questionData).length > 0) {
              setProceed(true);
              setExtractedQuestions(questionData);
            }
          } catch (error) {
            console.error('Error parsing question data:', error);
          }
        }
        // Only redirect to template/rubric if we're not already there
        // Check if current URL contains "create/template" or "create/rubric"
        else if (creation_step === 5 && !window.location.pathname.includes('/create/template')) {
          // Step 5 is PDFAssignmentTemplate - redirect to template page
          console.log('Redirecting to template editor (step 5)');
          navigate(`/tclasses/${class_id}/tassignments/${assignment_id}/create/template`);
        } else if (creation_step === 6 && !window.location.pathname.includes('/create/rubric')) {
          // Step 6 is PDFRubricGenerator - redirect to rubric page
          console.log('Redirecting to rubric editor (step 6)');
          navigate(`/tclasses/${class_id}/tassignments/${assignment_id}/create/rubric`);
        }
      } catch (error) {
        console.error('Error fetching progress for redirect:', error);
      }
    };

    redirectBasedOnProgress();
  }, [assignment_id, navigate, class_id, user.session_token]);

  useEffect(() => {
    getAssignment();

    // Load saved progress when component mounts
    loadSavedProgress();

    return () => {
      dispatch(clearAssignment());
      dispatch(clearSubmissions());
    };
  }, []);

  useEffect(() => {
    if (assignment && assignment.is_assignment_published) {
      navigate(`/tclasses/${class_id}/tassignments/${assignment_id}/tsubmissions`);
    } else if (assignment) {
      // Check for saved progress data and apply it if available
      if (assignment.creation_step && !hasLoadedProgress) {
        applyLoadedProgressData(assignment);
      }
    }
  }, [assignment]);

  // Track if we've already applied loaded progress to avoid infinite loops
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);
  const [activeTab, setActiveTab] = useState('questions');
  const [extractedQuestions, setExtractedQuestions] = useState(null);
  const [loadingSteps, setLoadingSteps] = useState([
    {
      id: 1,
      label: 'Parsing Document',
      icon: FileText,
      progress: 0,
      status: 'pending'
    },
    {
      id: 2,
      label: 'Extracting Questions',
      icon: Code,
      progress: 0,
      status: 'pending'
    },
    {
      id: 3,
      label: 'Analyzing Structure',
      icon: Cpu,
      progress: 0,
      status: 'pending'
    },
    {
      id: 4,
      label: 'Generating Rubrics',
      icon: CheckCircle,
      progress: 0,
      status: 'pending'
    }
  ]);
  const [currentLoadingStep, setCurrentLoadingStep] = useState(1);

  // Function to load saved progress from the server
  const loadSavedProgress = async () => {
    try {
      const response = await gradeApi.get({
        path: `/api/assignment/${assignment_id}/load-progress/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      // Apply the loaded progress data
      if (response.data) {
        applyLoadedProgressData(response.data);
      }
    } catch (error) {
      console.error('Error loading progress:', error);
      // Don't show an error toast - it's ok if there's no saved progress yet
    }
  };

  // Apply loaded progress data
  const applyLoadedProgressData = progressData => {
    console.log('Applying loaded progress data:', progressData);

    if (progressData.solution_pdf_url) {
      // Set the solution PDF if available
      setPdfFiles(prev => ({
        ...prev,
        withSolutions: progressData.solution_pdf_url
      }));
      setUploadStatus(prev => ({ ...prev, withSolutions: true }));
    }

    // If question boxes were saved, restore them
    if (progressData.question_boxes) {
      try {
        // Check if we have extracted questions data
        if (typeof progressData.question_boxes === 'string') {
          const questionData = JSON.parse(progressData.question_boxes);
          setExtractedQuestions(questionData);
        } else if (typeof progressData.question_boxes === 'object') {
          setExtractedQuestions(progressData.question_boxes);
        }

        if (progressData.creation_step === 4) {
          // Make sure proceed is set to true for RubricViewer
          setProceed(true);
        }
      } catch (error) {
        console.error('Error processing question boxes:', error);
      }
    }

    setHasLoadedProgress(true);
  };

  // Function to save progress with a specific step
  const saveProgress = async targetStep => {
    try {
      // Use provided targetStep if available, otherwise use 3 (single step workflow)
      let creationStep = targetStep !== undefined ? targetStep : 3;

      // If we're showing RubricViewer (proceed=true and extractedQuestions),
      // save the creation_step as 4 to indicate RubricViewer
      if (proceed && extractedQuestions) {
        creationStep = 4;
        console.log('Saving as RubricViewer (step 4)');
      }

      console.log('Saving progress with creation step:', creationStep);

      // Prepare the progress data
      const progressData = {
        creation_step: creationStep
      };

      // Check if solution PDF is a base64 string and upload to S3 if needed
      if (pdfFiles?.withSolutions) {
        if (
          typeof pdfFiles.withSolutions === 'string' &&
          pdfFiles.withSolutions.startsWith('data:application/pdf;base64,')
        ) {
          console.log('Uploading solution PDF to S3...');
          try {
            // Upload the base64 PDF to S3
            const response = await temporaryUpload(pdfFiles.withSolutions);
            if (response?.data?.success && response?.data?.url) {
              console.log('Solution PDF uploaded to S3, URL:', response.data.url);
              progressData.solution_pdf_url = response.data.url;
            } else {
              throw new Error('Failed to upload solution PDF to S3');
            }
          } catch (uploadError) {
            console.error('Error uploading solution PDF:', uploadError);
            // Fall back to using base64 if upload fails
            progressData.solution_pdf_url = pdfFiles.withSolutions;
          }
        } else {
          // Already an URL, just use it
          progressData.solution_pdf_url = pdfFiles.withSolutions;
        }
      }

      // Save question boxes if available
      if (extractedQuestions) {
        progressData.question_boxes = extractedQuestions;
      }

      await gradeApi.post({
        path: `/api/assignment/${assignment_id}/save-progress/`,
        body: progressData,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      toast.success('Progress saved successfully!');
      return true;
    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error('Failed to save progress. Please try again.');
      return false;
    }
  };

  // Function to publish the assignment and mark it as finalized
  const publishAssignment = async updatedQuestions => {
    setIsPublishing(true);

    try {
      // Save progress indicating "published" state
      const progressData = {
        creation_step: 7, // Indicate published state
        rubrics_generated: true,
        is_published: true,
        question_boxes: updatedQuestions
      };

      await gradeApi.post({
        path: `/api/assignment/${assignment_id}/save-progress/`,
        body: progressData,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`,
            'Content-Type': 'application/json'
          }
        }
      });

      // Call the build-assignment endpoint to build the assignment PDF context
      const buildResponse = await gradeApi.post({
        path: `/api/assignment/${assignment_id}/build-assignment/`,
        body: {
          instructions: updatedQuestions.instructions?.text || null,
          exam_paper: Object.entries(updatedQuestions)
            .filter(([key]) => key !== 'instructions') // Filter out the instructions object by key
            .map(([_, question]) => {
              // Create a properly formatted question object for the backend
              const formattedQuestion = {
                question: question.text || '',
                imageS3Key: question.imageS3Key || null,
                imageSize: question.imageSize || null,
                rubricItems: question.rubricItems || []
              };

              // If it has subquestions, format them correctly
              if (question.subquestions && question.subquestions.length > 0) {
                formattedQuestion.subQuestions = question.subquestions.map(sub => ({
                  question: sub.text || '',
                  solution: sub.solution || '',
                  weight: sub.weight || 1, // Important: Include the weight value
                  questionType: sub.type || 'freeform',
                  imageS3Key: sub.imageS3Key || null,
                  imageSize: sub.imageSize || null,
                  mcqOptions: sub.options || [],
                  rubricItems: sub.rubricItems || []
                }));
              } else {
                // For questions without subquestions, set the weight directly
                formattedQuestion.weight = question.weight || 1;
                formattedQuestion.solution = question.solution || '';
              }

              return formattedQuestion;
            }),
          preserve_questions: false
        },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`,
            'Content-Type': 'application/json'
          }
        }
      });

      if (buildResponse.status !== 200) {
        throw new Error(`Build assignment request failed with status: ${buildResponse.status}`);
      }

      // Then publish the assignment
      const publishResponse = await gradeApi.post({
        path: `/api/assignment/${assignment_id}/publish-assignment/`,
        body: {
          is_assignment_published: true
        },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`,
            'Content-Type': 'application/json'
          }
        }
      });

      if (publishResponse.status !== 200) {
        throw new Error(`Publish request failed with status: ${publishResponse.status}`);
      }

      // Clear the flag as this assignment is now complete
      localStorage.removeItem('rubricsGenerated');
      localStorage.removeItem('assignment_id');

      // Add a small delay before navigating to ensure state is updated
      setTimeout(() => {
        navigate(`/tclasses/${class_id}/tassignments/${assignment_id}/tsubmissions`);
      }, 500);
    } catch (error) {
      console.error('Error publishing assignment:', error);
      setIsPublishing(false);
      toast.error('There was an error publishing the assignment. Please try again.');
    }
  };

  // Function to handle auto-save at key points
  const autoSaveProgress = targetStep => {
    // Don't show toast messages for auto-save
    saveProgress(targetStep).catch(err => console.error('Auto-save failed:', err));
  };

  const handleSolutionFileUpload = async e => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setIsUploading(prev => ({ ...prev, withSolutions: true }));
      setUploadedFile(file);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        const base64String = reader.result;
        try {
          const response = await temporaryUpload(base64String);
          if (response?.data?.success) {
            // We don't store PDFs in localStorage anymore to avoid confusion between assignments
            setPdfFiles(prev => ({
              ...prev,
              withSolutions: base64String
            }));
            setUploadStatus(prev => ({
              ...prev,
              withSolutions: true
            }));
          }
        } catch (error) {
          console.error('Error uploading solutions PDF:', error);
        } finally {
          setIsUploading(prev => ({ ...prev, withSolutions: false }));
        }
      };
    }
  };

  async function temporaryUpload(base64) {
    try {
      return await gradeApi.post({
        path: `api/assignment/byob-problem-set/upload-base64-pdf/`,
        body: { file: base64 },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`,
            'Content-Type': 'application/json'
          }
        }
      });
    } catch (error) {
      console.error('Error in temporaryUpload:', error);
      throw error;
    }
  }

  const handleDragOver = e => {
    e.preventDefault();
    setUploadHover(true);
  };

  const handleDragLeave = () => {
    setUploadHover(false);
  };

  const handleDrop = async e => {
    e.preventDefault();
    setUploadHover(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/pdf') {
      await handleSolutionFileUpload({ target: { files: [file] } });
    }
  };

  // Modified function to handle proceeding with solutions and saving progress
  const handleProcessSolutions = async () => {
    if (uploadStatus.withSolutions) {
      // Save progress as step 4 (RubricViewer)
      await saveProgress(4);
      // Then proceed
      setProceed(true);
    } else {
      toast.error('Please upload the solution PDF first.');
    }
  };

  // Navigate back to the assignments page
  const handleBackToAssignments = () => {
    navigate(`/tclasses/${class_id}/tassignments/`);
  };

  // Add useEffect to redirect to generate-rubric page if progress indicates step 5 or higher
  useEffect(() => {
    // Check if we're already in a PDF upload workflow (proceed === true)
    // If proceed is true, we're already in the RubricViewer, so no need to redirect
    if (proceed) {
      return;
    }

    // Only redirect if we have question data but are not yet in the RubricViewer
    if (extractedQuestions && Object.keys(extractedQuestions).length > 0 && !proceed) {
      // If we have step 5 in progress data, redirect to template page
      if (assignment?.creation_step === 5) {
        navigate(`/tclasses/${class_id}/tassignments/${assignment_id}/create/template`);
      }
      // If we have step 6 in progress data, redirect to rubric page
      else if (assignment?.creation_step === 6) {
        navigate(`/tclasses/${class_id}/tassignments/${assignment_id}/create/rubric`);
      }
    }
  }, [extractedQuestions, assignment, class_id, assignment_id, navigate, proceed]);

  const updateLoadingProgress = (stepId, progress, status = 'processing') => {
    setLoadingSteps(steps =>
      steps.map(step => (step.id === stepId ? { ...step, progress, status } : step))
    );
  };

  return (
    <div className="app solution-rubrics">
      {/* Only show header when not showing RubricViewer */}
      {!proceed && (
        <div className="page-header" style={{ 
          height: 'min(70px, 10vh)',
          display: 'flex',
          alignItems: 'center'
        }}>
          <div className="header-container">
            <div className="header-left">
              <button className="back-button" onClick={handleBackToAssignments}>
                <ArrowLeft size={20} />
                <span>Back</span>
              </button>

              <div className="gradewiz-logo-container">
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/tclasses/${class_id}/tassignments/`)}
                >
                  <button className="save-exit-button">
                    <Save size={16} style={{ marginRight: '6px', color: '#c99b69' }} />
                    Save & Exit
                  </button>
                </div>
                <h1 style={{ fontSize: 'min(1.8rem, 4.5vw)' }}>Upload Answer Key</h1>
              </div>
            </div>

            <div className="step-indicator">GradeWiz 0.31</div>
          </div>
        </div>
      )}

      <div className="content-area" style={{ marginTop: proceed ? '0px' : '75px' }}>
        <input
          type="file"
          accept=".pdf"
          id="rubric-pdf-upload-solutions"
          className="hidden"
          style={{ display: 'none' }}
          onChange={handleSolutionFileUpload}
        />

        {/* Upload Solution PDF */}
        {!proceed && (
          <div className="wizard-step-container" style={{ 
            minHeight: 'min(calc(100vh - 120px), 600px)', 
            paddingBottom: 'min(50px, 4vh)',
            paddingTop: 'min(25px, 2vh)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <SolutionUploadComponent
              uploadHover={uploadHover}
              uploadStatus={uploadStatus}
              isUploading={isUploading}
              handleDragOver={handleDragOver}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              handleProcessSolutions={handleProcessSolutions}
              handleFileChange={handleSolutionFileUpload}
              showFileRestrictions={true}
              onShowExamples={() => setShowExamplesModal(true)}
              onShowEssayGuidance={() => setShowEssayGuidanceModal(true)}
            />
          </div>
        )}

        {/* PDF Processor and Rubric Viewer */}
        {proceed && (
          <div className="wizard-step-container" style={{
            height: '100vh',
            overflow: 'auto'
          }}>
            <PDFProcessor
              assignmentId={assignment_id}
              uploadedFile={uploadedFile}
              setIsProcessing={setIsProcessing}
              updateLoadingProgress={updateLoadingProgress}
              setCurrentLoadingStep={setCurrentLoadingStep}
              onProcessingComplete={draftData => {
                setExtractedQuestions(draftData);
                setIsProcessing(false);
                setLoadingSteps(steps =>
                  steps.map(step => ({ ...step, progress: 0, status: 'pending' }))
                );
                // Auto-save when processing completes
                // Save as step 4 (RubricViewer) when processing completes
                autoSaveProgress(4);
              }}
              onError={errorMessage => {
                console.error(errorMessage);
                setIsProcessing(false);
                toast.error(errorMessage);
              }}
            />
            <div className="rubric-viewer-wrapper">
              {/* Always render RubricViewer and let it handle the isProcessing state internally */}
              <RubricViewer
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isProcessing={isProcessing}
                loadingSteps={loadingSteps}
                extractedQuestions={extractedQuestions}
                setExtractedQuestions={setExtractedQuestions}
                currentLoadingStep={currentLoadingStep}
                pdfFiles={pdfFiles}
                setProceed={setProceed}
                assignment_data={assignment}
                onSave={saveProgress}
                onPublish={updatedQuestions => {
                  // Store the latest version of questions with instructions
                  setExtractedQuestions(updatedQuestions);
                  setIsModalOpen(true);
                }}
              />
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={isModalOpen}
        message="Finalizing this assignment will prevent you from making changes to it. It WON'T be visible to students until you publish their grades"
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => {
          publishAssignment(extractedQuestions);
          setIsModalOpen(false);
        }}
      />

      <ExamplesModal isOpen={showExamplesModal} onClose={() => setShowExamplesModal(false)} />
      <EssayGuidanceModal
        isOpen={showEssayGuidanceModal}
        onClose={() => setShowEssayGuidanceModal(false)}
      />
    </div>
  );
}
